/**
 * Role Queries
 * 
 * React Query hooks for role management.
 * Uses roleService for data operations (supports Mock and Gobodo backends).
 * 
 * Part of PE-894: Create roleService.ts with Gobodo CRUD
 * Part of PE-896: Wire roleQueries.ts to roleService
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { roleService } from '../roleService';
import { mockRoleProvider } from '../providers/mockRoleProvider';
import type {
  Role,
  RoleHierarchyNode,
  ObjectPermission,
  PermissionLevel,
  SearchPermissions,
  RoleFilters,
  CreateRoleRequest,
  UpdateRoleRequest,
} from '../providers/roleProvider';

export type {
  Role,
  RoleHierarchyNode,
  ObjectPermission,
  PermissionLevel,
  SearchPermissions,
  RoleFilters,
  CreateRoleRequest,
  UpdateRoleRequest,
};

// ============================================
// BACKWARD COMPATIBILITY EXPORTS
// ============================================

/**
 * Synchronous accessor for the current mock role set.
 * Reads from the same localStorage-backed store as mockRoleProvider.
 * Consumers: GlobalSearch, fieldPermissionQueries, SecurityHealthDashboard, userQueries.
 */
export function getMockRoles(): Role[] {
  try {
    const stored = localStorage.getItem('crm_roles_v2');
    if (stored) return JSON.parse(stored) as Role[];
  } catch { /* fall through */ }
  return [];
}

/** @deprecated — use `getMockRoles()` for a live snapshot. Kept for import compat. */
export const mockRoles: Role[] = getMockRoles();

export function resetRolesToDefaults(): void {
  mockRoleProvider.reset();
}

// Legacy: Available objects for permission assignment (hardcoded fallback)
export const availableObjects = [
  { id: 'obj_client', name: 'Client' },
  { id: 'obj_case', name: 'Case' },
  { id: 'obj_audit', name: 'Audit' },
  { id: 'obj_user', name: 'User' },
  { id: 'obj_contact', name: 'Contact' },
  { id: 'obj_account', name: 'Account' },
];

/**
 * Hook to fetch available objects for permission assignment from Gobodo
 * Falls back to hardcoded list if fetch fails
 */
export function useAvailableObjects() {
  return useQuery({
    queryKey: ['availableObjects'],
    queryFn: async () => {
      // Import here to avoid circular deps
      const { getGobodoObjectTypes } = await import('../gobodo/gobodoObjectService');
      
      try {
        const objects = await getGobodoObjectTypes();
        // Filter out Role object from the list (can't set permissions on roles)
        const filtered = objects.filter(obj => !obj.apiName.startsWith('Role'));
        return filtered.map(obj => ({
          id: obj.id,
          name: obj.label,
          apiName: obj.apiName,
        }));
      } catch (error) {
        console.warn('[roleQueries] Failed to fetch objects from Gobodo, using hardcoded list:', error);
        return availableObjects;
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes (object list changes rarely)
    placeholderData: availableObjects, // Show hardcoded list immediately
  });
}

// ============================================
// QUERY KEYS
// ============================================

export const roleKeys = {
  all: ['roles'] as const,
  lists: () => [...roleKeys.all, 'list'] as const,
  list: (filters: string) => [...roleKeys.lists(), filters] as const,
  details: () => [...roleKeys.all, 'detail'] as const,
  detail: (id: string) => [...roleKeys.details(), id] as const,
  hierarchy: () => [...roleKeys.all, 'hierarchy'] as const,
};

// ============================================
// HOOKS
// ============================================

/**
 * Get all roles with optional filtering
 * Uses roleService for data (supports Mock and Gobodo backends)
 */
export function useRoles(
  filters?: {
    searchQuery?: string;
    includeSystem?: boolean;
  },
  queryOptions?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: roleKeys.list(JSON.stringify(filters || {})),
    queryFn: async () => {
      return roleService.getRoles({
        searchQuery: filters?.searchQuery,
        includeSystem: filters?.includeSystem,
      });
    },
    staleTime: 2 * 60 * 1000,
    ...queryOptions,
  });
}

/**
 * Get single role by ID
 * Uses roleService for data (supports Mock and Gobodo backends)
 */
export function useRole(roleId: string) {
  return useQuery({
    queryKey: roleKeys.detail(roleId),
    queryFn: async () => {
      const role = await roleService.getRoleById(roleId);
      if (!role) {
        throw new Error('Role not found');
      }
      return role;
    },
    staleTime: 5 * 60 * 1000,
    enabled: !!roleId,
  });
}

/**
 * Get role hierarchy
 * Uses roleService for data (supports Mock and Gobodo backends)
 */
export function useRoleHierarchy() {
  return useQuery({
    queryKey: roleKeys.hierarchy(),
    queryFn: async () => {
      return roleService.getRoleHierarchy();
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Create new role
 * Uses roleService for data (supports Mock and Gobodo backends)
 */
export function useCreateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleData: Omit<Role, 'id' | 'createdBy' | 'createdDate' | 'lastModified' | 'userCount'>) => {
      return roleService.createRole({
        name: roleData.name,
        description: roleData.description,
        parentRoleId: roleData.parentRoleId,
        permissions: roleData.permissions,
        searchPermissions: roleData.searchPermissions,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.hierarchy() });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Update role
 * Uses roleService for data (supports Mock and Gobodo backends)
 */
export function useUpdateRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ roleId, updates }: { roleId: string; updates: Partial<Role> }) => {
      return roleService.updateRole(roleId, {
        name: updates.name,
        description: updates.description,
        parentRoleId: updates.parentRoleId,
        permissions: updates.permissions,
        searchPermissions: updates.searchPermissions,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.detail(data.id) });
      queryClient.invalidateQueries({ queryKey: roleKeys.hierarchy() });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Delete role
 * Uses roleService for data (supports Mock and Gobodo backends)
 */
export function useDeleteRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleId: string) => {
      await roleService.deleteRole(roleId);
      return { roleId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.hierarchy() });
      queryClient.invalidateQueries({ queryKey: ['setup', 'recent-changes'] });
    },
  });
}

/**
 * Clone role
 * Uses roleService for data (supports Mock and Gobodo backends)
 */
export function useCloneRole() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (roleId: string) => {
      // Default clone name is "Original Name (Copy)"
      const original = await roleService.getRoleById(roleId);
      const newName = original ? `${original.name} (Copy)` : 'Role (Copy)';
      return roleService.cloneRole(roleId, newName);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: roleKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roleKeys.hierarchy() });
    },
  });
}

// ============================================
// UTILITY FUNCTIONS
// ============================================

/**
 * Build role hierarchy tree
 */
export function buildRoleHierarchy(roles: Role[]): RoleHierarchyNode[] {
  const roleMap = new Map<string, RoleHierarchyNode>();
  const rootRoles: RoleHierarchyNode[] = [];

  // Initialize all roles with empty children
  roles.forEach(role => {
    roleMap.set(role.id, { ...role, children: [] });
  });

  // Build hierarchy
  roles.forEach(role => {
    const node = roleMap.get(role.id)!;

    if (role.parentRoleId) {
      const parent = roleMap.get(role.parentRoleId);
      if (parent) {
        parent.children.push(node);
      } else {
        rootRoles.push(node);
      }
    } else {
      rootRoles.push(node);
    }
  });

  return rootRoles;
}

/**
 * Get permission level for an object
 */
export function getPermissionLevel(permission: ObjectPermission): PermissionLevel {
  if (permission.delete) return 'delete';
  if (permission.write) return 'write';
  if (permission.read) return 'read';
  return 'none';
}

/**
 * Check if role has permission
 */
export function hasPermission(
  role: Role,
  objectId: string,
  level: 'read' | 'write' | 'delete'
): boolean {
  const permission = role.permissions.find(p => p.objectId === objectId);
  if (!permission) return false;

  return permission[level];
}

/**
 * Get all objects a role can access
 */
export function getAccessibleObjects(role: Role): string[] {
  return role.permissions
    .filter(p => p.read)
    .map(p => p.objectName);
}

/**
 * Count total permissions
 */
export function countPermissions(role: Role): { read: number; write: number; delete: number } {
  return role.permissions.reduce((acc, perm) => ({
    read: acc.read + (perm.read ? 1 : 0),
    write: acc.write + (perm.write ? 1 : 0),
    delete: acc.delete + (perm.delete ? 1 : 0)
  }), { read: 0, write: 0, delete: 0 });
}

// ============================================
// SEARCH PERMISSIONS UTILITIES
// ============================================

/**
 * Get default search permissions based on role name
 * Useful when creating new roles
 */
export function getDefaultSearchPermissions(roleName: string): Role['searchPermissions'] {
  const name = roleName.toLowerCase();
  const isAdmin = name.includes('admin') || name.includes('administrator');
  const isManager = name.includes('manager') || name.includes('supervisor');

  if (isAdmin) {
    return {
      canSearchObjects: true,
      canSearchFields: true,
      canSearchWorkflows: true,
      canSearchSystemSettings: true,
      canSearchUsers: true,
      canSearchRoles: true,
      canSearchSecurity: true,
      canSearchReports: true,
      canSearchDashboards: true,
      canSearchProfileSettings: true,
      canSearchClients: true,
      canSearchContacts: true,
      canSearchLeads: true,
      canSearchOpportunities: true,
      canSearchCases: true,
      canSearchFirms: true,
      canSearchCyberSweepClients: true,
      canSearchCSAudits: true,
      canSearchPAWRecords: true,
      canSearchCreditCases: true,
      canSearchTradelines: true,
      canSearchCreditBlockAccounts: true,
      visibleCategories: ['objects', 'workflows', 'fields', 'settings', 'reports', 'dashboards', 'profile', 'clients', 'contacts', 'leads', 'opportunities', 'cases', 'firms']
    };
  }

  if (isManager) {
    return {
      canSearchObjects: true,
      canSearchFields: true,
      canSearchWorkflows: true,
      canSearchSystemSettings: false,
      canSearchUsers: false,
      canSearchRoles: false,
      canSearchSecurity: false,
      canSearchReports: true,
      canSearchDashboards: true,
      canSearchProfileSettings: true,
      canSearchClients: true,
      canSearchContacts: true,
      canSearchLeads: true,
      canSearchOpportunities: true,
      canSearchCases: true,
      canSearchFirms: true,
      canSearchCyberSweepClients: true,
      canSearchCSAudits: true,
      canSearchPAWRecords: true,
      canSearchCreditCases: true,
      canSearchTradelines: true,
      canSearchCreditBlockAccounts: true,
      visibleCategories: ['objects', 'workflows', 'fields', 'reports', 'dashboards', 'profile', 'clients', 'contacts', 'leads', 'opportunities', 'cases', 'firms']
    };
  }

  // Default: Standard User - can search records and profile, but not admin features
  return {
    canSearchObjects: true,
    canSearchFields: false,
    canSearchWorkflows: false,
    canSearchSystemSettings: false,
    canSearchUsers: false,
    canSearchRoles: false,
    canSearchSecurity: false,
    canSearchReports: false,
    canSearchDashboards: false,
    canSearchProfileSettings: true,
    canSearchClients: true,
    canSearchContacts: true,
    canSearchLeads: true,
    canSearchOpportunities: true,
    canSearchCases: true,
    canSearchFirms: true,
    canSearchCyberSweepClients: false,
    canSearchCSAudits: false,
    canSearchPAWRecords: false,
    canSearchCreditCases: false,
    canSearchTradelines: false,
    canSearchCreditBlockAccounts: false,
    visibleCategories: ['objects', 'profile', 'clients', 'contacts', 'leads', 'opportunities', 'cases', 'firms']
  };
}

/**
 * Check if a role has permission to search a specific category
 */
export function canRoleSearch(
  role: Role | undefined,
  category: 'objects' | 'workflows' | 'fields' | 'settings'
): boolean {
  if (!role?.searchPermissions) {
    return false;
  }
  return role.searchPermissions.visibleCategories.includes(category);
}

/**
 * Get a human-readable description of search permissions
 */
export function getSearchPermissionsDescription(role: Role): string {
  if (!role.searchPermissions) {
    return 'No search access';
  }

  const categories = role.searchPermissions.visibleCategories;
  if (categories.length === 0) {
    return 'No search access';
  }

  const categoryNames: Record<string, string> = {
    objects: 'Objects',
    workflows: 'Workflows',
    fields: 'Fields',
    settings: 'Settings',
    reports: 'Reports',
    dashboards: 'Dashboards',
    profile: 'Profile',
    clients: 'Clients',
    contacts: 'Contacts',
    leads: 'Leads',
    opportunities: 'Opportunities',
    cases: 'Cases',
    firms: 'Firms'
  };

  // Check for full access (all categories)
  if (categories.length >= Object.keys(categoryNames).length) {
    return 'Full search access';
  }

  return `Can search: ${categories.map(c => categoryNames[c] || c).join(', ')}`;
}
